import {Injectable} from "@angular/core";
import {isNullOrUndefined} from "util";
import {Router} from "@angular/router";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {StoreService} from "../store/store.service";
import {Observable} from "rxjs";
import {NotificationManager} from "../../../../common/providers/notification";
import {map, switchMap, tap} from "rxjs/operators";

export enum Roles {
    VIEW, EDIT
}

export interface Element {
    id: string;
    role: string;
}

export interface Permission {
    role: Roles;
    route: string;
    elements: Array<Element>;
}

@Injectable()
export class RoleService {

    private static readonly PERMISSIONS: 'PERMISSIONS';

    constructor(private router: Router, private http: HttpClient,
                private _notificationManager: NotificationManager) {
    }

    private permissions: Array<Permission> = [];

    public getPermissions(): Array<Permission> {
        if (this.permissions.length <= 0) {
            this.permissions = StoreService.getInstance().getValue(RoleService.PERMISSIONS);
        }
        return this.permissions;
    }

    public hasElementRoles(role: Roles, element: string, route?: string) {

        if (isNullOrUndefined(role) || isNullOrUndefined(element) ) {
            return false;
        }
        if (isNullOrUndefined(route)) {
            route = this.router.url;
        }

        let p =  this.getPermission(route);
        return p.elements.filter(el=> el.id  == element && RoleService.getRoleFromValue(el.role) == role ).length>0
    }


    public hasRoles(roles: string | Roles | Array<string> | Array<Roles>, route?: string): boolean {
        if (isNullOrUndefined(roles)) {
            return false;
        }

        if (isNullOrUndefined(route)) {
            route = this.router.url;
        }
        if (Array.isArray(roles)) {
            return (roles as Array<any>).some(role => this.hasRole(role, route));
        } else {
            return this.hasRole(roles, route);
        }
    }

    public hasAccess(route: string): boolean {
        if (isNullOrUndefined(route)) {
            return false;
        }
        const permission = this.getPermission(route);
        return !isNullOrUndefined(permission) && !isNullOrUndefined(permission.role);
    }

    /**
     * Like hasRoles/hasAccess, but against the permission of exactly this menu route (e.g.
     * '/space/customers/profiles/registered/:id/users'). getPermission() takes the first permission whose route
     * is a prefix of the URL, so '/space/customers/profiles/registered' (EDIT) wins over a tab granted VIEW only.
     */
    public hasExactRouteRoles(roles: string | Roles | Array<string> | Array<Roles>, permissionRoute: string): boolean {
        if (isNullOrUndefined(roles)) {
            return false;
        }
        const permission = this.getExactPermission(permissionRoute);
        if (isNullOrUndefined(permission) || isNullOrUndefined(permission.role)) {
            return false;
        }
        const required = Array.isArray(roles) ? roles as Array<any> : [roles];
        return required.some(role => permission.role >= RoleService.getRoleFromValue(role));
    }

    public hasExactRouteAccess(permissionRoute: string): boolean {
        const permission = this.getExactPermission(permissionRoute);
        return !isNullOrUndefined(permission) && !isNullOrUndefined(permission.role);
    }

    private getExactPermission(permissionRoute: string): Permission {
        if (isNullOrUndefined(permissionRoute)) {
            return undefined;
        }
        return this.dedupePermissionsByRoute(this.getPermissions() || [])
            .find(permission => permission.route === permissionRoute);
    }

    private getPermission(route: string): Permission {
        return this.dedupePermissionsByRoute(this.getPermissions())
            .filter((permission, index, arrayPermissions) => {
                let amount = 0
                arrayPermissions.forEach(perm => {
                    if (!perm.route.includes('/:id')
                        && perm.route.startsWith(permission.route)) {
                        amount += 1;
                    }
                });
                return amount <= 1;
            })
            .find(permission => {
                const permissionRegex = new RegExp('^' + permission.route.replace('/:id','/[0-9]+'));
                return permissionRegex.test(route);
            });
    }

    /**
     * A user can end up with the same route granted more than once (e.g. member of two profiles/roles
     * that both include the same menu) - the backend then returns duplicate entries for that exact
     * route. The parent/child prefix filter in getPermission() counts a route against itself via
     * String.startsWith, so an exact duplicate makes it look like there's a more specific child route
     * and the entry gets wrongly discarded, denying access despite the permission being granted.
     * Collapsing to one entry per route (keeping the highest role) before that filter runs fixes it.
     */
    private dedupePermissionsByRoute(permissions: Array<Permission>): Array<Permission> {
        const byRoute = new Map<string, Permission>();
        permissions.forEach(permission => {
            const existing = byRoute.get(permission.route);
            if (isNullOrUndefined(existing) || permission.role > existing.role) {
                byRoute.set(permission.route, permission);
            }
        });
        return Array.from(byRoute.values());
    }

    private hasRole(role: string | Roles, route: string): boolean {
        const permission = this.getPermission(route);
        if (isNullOrUndefined(permission)) {
            return false;
        }
        role = RoleService.getRoleFromValue(role)
        return permission.role >= role;
    }


    public hasMenuAccess(route: string): boolean {
        if (isNullOrUndefined(route)) {
            return false;
        }

        const permission = this.getMenuPermission(route);
        return !isNullOrUndefined(permission) && !isNullOrUndefined(permission.role);
    }

    private getMenuPermission(route: string): Permission {
        return this.getPermissions().find(permission => {
            return permission.route.startsWith(route);
        });
    }

    private hasMenuRole(role: string | Roles, route: string): boolean {
        const permission = this.getMenuPermission(route);
        if (isNullOrUndefined(permission)) {
            return false;
        }
        return permission.role === RoleService.getRoleFromValue(role);
    }

    public hasMenuRoles(roles: string | Roles | Array<string> | Array<Roles>, route?: string): boolean {
        if (isNullOrUndefined(roles)) {
            return false;
        }

        if (isNullOrUndefined(route)) {
            route = this.router.url;
        }

        if (Array.isArray(roles)) {
            return (roles as Array<any>).some(role => this.hasMenuRole(role, route));
        } else {
            return this.hasMenuRole(roles, route);
        }
    }


    public static getRoleFromValue(role: string | Roles): Roles {
        if (isNullOrUndefined(role)) {
            return null;
        }

        if (typeof role === 'string') {
            return Roles[role.toUpperCase()]
        } else {
            return role;
        }
    }

    public resetPermissions() {
        this.permissions.length = 0;
        StoreService.getInstance().deleteValue(RoleService.PERMISSIONS);
    }

    public fetchPermissions(): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({
                'username': 'admin@admin.com',
            })
        };

        return this.http.get('api/backoffice/menu', httpOptions).pipe(
            map(response => {
                const permissions = (response as Array<any>).map(permission => {
                    return {
                        route: permission.route,
                        role: RoleService.getRoleFromValue(permission.role),
                        elements: permission.elements
                    } as Permission;
                });
                StoreService.getInstance().setValue(RoleService.PERMISSIONS, permissions);
                return permissions;
            }),
            switchMap(() => {
                return this.http.get<{
                    message: { [key: string]: number }
                }>('api/backoffice/notification/payments', httpOptions);
            }),
            tap(secondResponse => {
                const messageMap = secondResponse.message;

                if (messageMap && Object.keys(messageMap).length > 0) {
                    const formattedMessage = `There are payments to review, these are their statuses:<br>` +
                        Object.keys(messageMap)
                            .map(key => `- ${key}: ${messageMap[key]}`)
                            .join('<br>');

                    this._notificationManager.error(formattedMessage, 20000);
                }
            }),
            map(secondResponse => {
                return secondResponse;
            })
        );
    }

    public arePermissionsLoaded(): boolean {
        return !isNullOrUndefined(StoreService.getInstance().getValue(RoleService.PERMISSIONS));
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./src/dashboard/app/core/security/role.service.ts